import React, { useEffect, useRef, useMemo, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Button, CheckedIcon, Row, Spinner, Table, TextField, Typography } from "@coolremote/shared";
import { useCreateModbusFunctionalTemplate, useGetSiteModbusFunctionalTemplates } from "../../../../hooks/useModbusFunctionalTemplatesApi";
import { IModbusFunctionalTemplate } from "../../types";
import styles from "./ImportFromLibraryDialog.module.css";

interface IProps {
  open: boolean;
  siteId?: string;
  /** IDs of functional templates already loaded into the device draft. */
  loadedTemplateIds: Set<string>;
  onClose: () => void;
  onLoad: (template: IModbusFunctionalTemplate) => void;
  onImportSuccess?: () => void;
}

const ImportFromLibraryDialog: React.FC<IProps> = ({
  open,
  siteId,
  loadedTemplateIds,
  onClose,
  onLoad,
  onImportSuccess,
}) => {
  const [search, setSearch] = useState("");
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createFunctionalTemplate } = useCreateModbusFunctionalTemplate({});
  // Self-load the library list; reload it fresh each time the modal opens.
  const { data, isLoading, refetch } = useGetSiteModbusFunctionalTemplates(siteId, open) as unknown as {
    data: IModbusFunctionalTemplate[] | null;
    isLoading: boolean;
    refetch: () => void;
  };
  const templates = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !siteId) return;
    e.target.value = "";

    const text = await file.text();
    let items: IModbusFunctionalTemplate[];
    try {
      const parsed = JSON.parse(text);
      items = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return;
    }

    setImportProgress({ done: 0, total: items.length });
    for (let i = 0; i < items.length; i++) {
      try {
        await createFunctionalTemplate({ ...items[i], site: siteId });
      } catch {
        // skip this item, keep importing the rest
      }
      setImportProgress({ done: i + 1, total: items.length });
    }
    setImportProgress(null);
    refetch();
    onImportSuccess?.();
  };

  const importing = importProgress !== null;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t`Functional Unit Library`}
      hideConfirm
      cancelLabel={t`Close`}
      cancelSize="medium"
      size="large"
    >
      <div className={styles.searchWrap}>
        <div className={styles.searchField}>
          <TextField
            fullWidth
            placeholder={t`Search by name`}
            value={search}
            onChange={(v: string) => setSearch(v)}
          />
        </div>
        <Row gap={8} align="center">
          {siteId && (
            importing ? (
              <div className={styles.importProgress}>
                <Spinner size={14} />
                <Typography variant="meta">
                  {t`Importing`} {importProgress.done} / {importProgress.total}…
                </Typography>
              </div>
            ) : (
              <>
                <Button white size="medium" onClick={() => fileInputRef.current?.click()}>
                  {t`Import`}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </>
            )
          )}
        </Row>
      </div>

      {isLoading ? (
        <div className={styles.loaderWrap}>
          <Spinner size={28} />
        </div>
      ) : (
      <Table
        data={filtered}
        getRowKey={(row: IModbusFunctionalTemplate, index: number) => row.id ?? index}
        emptyMessage={search ? t`No templates match your search.` : t`No functional unit templates found for this site.`}
        columns={[
          {
            id: "name",
            title: t`UNIT NAME`,
            width: "25%",
            render: (row: IModbusFunctionalTemplate) => row.name,
          },
          {
            id: "registers",
            title: t`REGISTERS COUNT`,
            width: "15%",
            render: (row: IModbusFunctionalTemplate) => (row.rows?.length ?? 0) + " " + t`registers`,
          },
          {
            id: "parameters",
            title: t`PARAMETERS`,
            width: "30%",
            render: (row: IModbusFunctionalTemplate) => (
              <div className={styles.paramList}>
                {(row.rows ?? []).map((r, i) => r.name).join(", ")}
              </div>
            ),
          },
          {
            id: "controlUi",
            title: t`CONTROL UI`,
            width: "10%",
            align: "center" as const,
            render: (row: IModbusFunctionalTemplate) =>
              row.defaultUiTemplate ? <CheckedIcon width={16} height={16} /> : <span>–</span>,
          },
          {
            id: "action",
            width: "20%",
            align: "right" as const,
            render: (row: IModbusFunctionalTemplate) => {
              const alreadyLoaded = !!row.id && loadedTemplateIds.has(row.id);
              return alreadyLoaded ? (
                <span className={styles.loadedBadge}>{t`Added`}</span>
              ) : (
                <Button white size="small" onClick={() => onLoad(row)}>
                  {t`Select`}
                </Button>
              );
            },
          },
        ]}
      />
      )}
    </BaseDialog>
  );
};

export default ImportFromLibraryDialog;
